import React from 'react';
import { MenuItem, ListSubheader } from '@mui/material';

// BC-style Chart of Accounts grouping for GL dropdowns. Accounts are grouped by
// category (Assets / Liabilities / Equity / Income / Expenses) under subheaders,
// and by default only *Posting* accounts are selectable — Heading/Total rows are
// structure, never postable.
export const ACCOUNT_GROUPS = [
  ['Asset',     'Assets'],
  ['Liability', 'Liabilities'],
  ['Equity',    'Equity'],
  ['Revenue',   'Income'],
  ['Expense',   'Expenses'],
];

/**
 * Build grouped <MenuItem>/<ListSubheader> options for a GL account <TextField select>.
 * @param {Array} accounts  - list from useGLAccounts()
 * @param {Object} opts
 * @param {boolean} opts.postingOnly  - only Posting accounts (default true)
 * @param {boolean} opts.includeNone  - leading "— Not set —" item (default true)
 * @param {string}  opts.noneLabel    - label for the none item
 * @returns {Array<React.ReactElement>}
 */
export function glAccountOptions(accounts, opts = {}) {
  const { postingOnly = true, includeNone = true, noneLabel = '— Not set —' } = opts;
  const list = (accounts || []).filter(a =>
    a.isActive !== false && (!postingOnly || (a.postingType || 'Posting') === 'Posting')
  );

  const els = [];
  if (includeNone) els.push(<MenuItem key="__none" value=""><em>{noneLabel}</em></MenuItem>);

  const used = new Set();
  ACCOUNT_GROUPS.forEach(([type, label]) => {
    const inGroup = list.filter(a => a.accountType === type)
      .sort((a, b) => String(a.accountNo).localeCompare(String(b.accountNo)));
    if (!inGroup.length) return;
    els.push(<ListSubheader key={`__h_${type}`} sx={{ lineHeight: '2em', fontWeight: 700 }}>{label}</ListSubheader>);
    inGroup.forEach(a => {
      used.add(a.accountNo);
      els.push(
        <MenuItem key={a.accountNo} value={a.accountNo} sx={{ pl: 3 }}>
          {a.accountNo} — {a.accountName}
        </MenuItem>
      );
    });
  });

  const others = list.filter(a => !used.has(a.accountNo))
    .sort((a, b) => String(a.accountNo).localeCompare(String(b.accountNo)));
  if (others.length) {
    els.push(<ListSubheader key="__h_other" sx={{ lineHeight: '2em', fontWeight: 700 }}>Other</ListSubheader>);
    others.forEach(a => els.push(
      <MenuItem key={a.accountNo} value={a.accountNo} sx={{ pl: 3 }}>{a.accountNo} — {a.accountName}</MenuItem>
    ));
  }
  return els;
}

export default glAccountOptions;
